import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NewsletterSignup from '../components/NewsletterSignup';
import ResourceCard from '../components/ResourceCard';
import SEO from '../components/SEO';
import { GenZFeature } from '../components/genz/GenZFeature';
import { BookOpen, Headphones, Video, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [featuredResources, setFeaturedResources] = useState([]);
  const [latestResources, setLatestResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const { data } = await axios.get(`${API}/resources?limit=6`);
      setFeaturedResources(data.slice(0, 3));
      setLatestResources(data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <SEO 
        title="RestoreMyWord - Biblical Stories and Resources for Gen Z"
        description="Empowering Gen Zers ages 10-20 to become mighty men and women of faith through Bible-based stories, audio teachings, video resources, and downloadable PDFs. Free biblical resources for every generation."
        url="https://restoremyword.net/"
      />
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/13036981/pexels-photo-13036981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-semibold tracking-tighter leading-tight text-white mb-6 fade-up" data-testid="hero-title">
            Restore My Word
          </h1>
          <p className="font-body text-xl text-white/90 mb-10 max-w-2xl mx-auto fade-up stagger-1" data-testid="hero-subtitle">
            Biblical Stories and Resources for Every Generation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center fade-up stagger-2">
            <button
              onClick={() => navigate('/library/story')}
              className="px-6 py-3 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-all duration-300"
              data-testid="hero-browse-stories-button"
            >
              Browse Stories
            </button>
            <button
              onClick={() => navigate('/library/audio')}
              className="px-6 py-3 bg-transparent border-2 border-white text-white font-body font-medium rounded hover:bg-white hover:text-brand-primary transition-all duration-300"
              data-testid="hero-listen-audio-button"
            >
              Listen to Audio
            </button>
            <button
              onClick={() => navigate('/library/video')}
              className="px-6 py-3 bg-transparent border-2 border-white text-white font-body font-medium rounded hover:bg-white hover:text-brand-primary transition-all duration-300"
              data-testid="hero-watch-videos-button"
            >
              Watch Videos
            </button>
          </div>
        </div>
      </section>

      <GenZFeature />

      {/* Featured Resources Section */}
      <section className="py-24 md:py-32" data-testid="featured-resources-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-accent mb-4 block">
              EXPLORE
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-brand-primary mb-4">
              Featured Resources
            </h2>
            <p className="font-body text-base leading-relaxed text-slate-700 max-w-2xl mx-auto">
              Discover our most popular Bible stories, teachings, and resources.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="font-body text-slate-600">Loading resources...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredResources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mission Section */}
      <section 
        className="py-24 md:py-32 relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/29286046/pexels-photo-29286046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="mission-section"
      >
        <div className="absolute inset-0 bg-brand-primary/85"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-white mb-6">
            Our Mission
          </h2>
          <p className="font-body text-lg leading-relaxed text-white/90">
            To empower Gen Zers ages 10–20 to become mighty men and women of faith who stand against the evil of 
            this generation and help restore God's truth, love, and holiness. Through God's anointing, we share 
            Bible-based stories that reveal His reality, power, grace, and transforming love—encouraging young hearts 
            to grow deeper in Christ and boldly live out their faith in today's world.
          </p>
        </div>
      </section>

      {/* Latest Additions Section */}
      <section className="py-24 md:py-32 bg-background-tertiary" data-testid="latest-additions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-accent mb-4 block">
              NEW
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-brand-primary mb-4">
              Latest Additions
            </h2>
            <p className="font-body text-base leading-relaxed text-slate-700 max-w-2xl mx-auto">
              Recently added resources to help you grow in faith and understanding.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {latestResources.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/library/story')}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-brand-primary text-white font-body font-medium rounded hover:bg-brand-primary-hover transition-all duration-300"
              data-testid="view-all-resources-button"
            >
              <span>View All Resources</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Browse by Type Section */}
      <section className="py-24 md:py-32" data-testid="browse-by-type-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-brand-primary mb-4">
              Browse by Type
            </h2>
            <p className="font-body text-base leading-relaxed text-slate-700 max-w-2xl mx-auto">
              Choose how you want to experience God's Word today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Story Library */}
            <div 
              onClick={() => navigate('/library/story')}
              className="bg-white border border-slate-200 rounded shadow-sm p-8 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              data-testid="browse-story-card"
            >
              <div className="w-16 h-16 bg-brand-accent/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-accent/20 transition-colors duration-300">
                <BookOpen className="h-8 w-8 text-brand-accent" />
              </div>
              <h3 className="font-heading font-medium text-xl text-brand-primary mb-3">
                Story Library
              </h3>
              <p className="font-body text-base text-slate-700 mb-4">
                Read biblical stories and teachings in text format.
              </p>
              <span className="text-sm font-medium text-brand-accent group-hover:text-brand-accent-hover">
                Explore Stories →
              </span>
            </div>

            {/* Audio Library */}
            <div 
              onClick={() => navigate('/library/audio')}
              className="bg-white border border-slate-200 rounded shadow-sm p-8 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              data-testid="browse-audio-card"
            >
              <div className="w-16 h-16 bg-brand-accent/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-accent/20 transition-colors duration-300">
                <Headphones className="h-8 w-8 text-brand-accent" />
              </div>
              <h3 className="font-heading font-medium text-xl text-brand-primary mb-3">
                Audio Library
              </h3>
              <p className="font-body text-base text-slate-700 mb-4">
                Listen to teachings and stories on the go.
              </p>
              <span className="text-sm font-medium text-brand-accent group-hover:text-brand-accent-hover">
                Listen Now →
              </span>
            </div>

            {/* Video Library */}
            <div 
              onClick={() => navigate('/library/video')}
              className="bg-white border border-slate-200 rounded shadow-sm p-8 text-center hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              data-testid="browse-video-card"
            >
              <div className="w-16 h-16 bg-brand-accent/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-accent/20 transition-colors duration-300">
                <Video className="h-8 w-8 text-brand-accent" />
              </div>
              <h3 className="font-heading font-medium text-xl text-brand-primary mb-3">
                Video Library
              </h3>
              <p className="font-body text-base text-slate-700 mb-4">
                Watch video teachings and visual stories.
              </p>
              <span className="text-sm font-medium text-brand-accent group-hover:text-brand-accent-hover">
                Watch Videos →
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-background-tertiary" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <NewsletterSignup />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;